import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LanguageService } from '../../../core/services/language.service';
import { DsSelectComponent, DsSelectOptionComponent, DsSelectText, } from '@assecods/ng-design-system';
import { ThemeService } from '../../../core/services/theme.service';

type LangItem = { lang: string; label: string };

@Component({
  selector: 'idh-language',
  templateUrl: './language.component.html',
  styleUrl: './language.component.scss',
  providers: [FormGroupDirective],
  imports: [ReactiveFormsModule, DsSelectComponent, DsSelectOptionComponent, DsSelectText],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'idh-language',
  },
})
export class LanguageComponent implements OnInit {
  protected readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly themeService = inject(ThemeService);
  private readonly theme = this.themeService.themeSig;
  readonly langControl = new FormControl<string>(
    this.languageService.currentLang()?.toLowerCase() ??
      this.languageService.getStoredLanguageData()?.currentLanguage?.toLowerCase() ??
      'pl',
    { nonNullable: true }
  );
  readonly items = signal<LangItem[]>([]);
  private readonly _langValue = signal(this.langControl.value);

  readonly showLanguageSelect = computed(() => {
    return this.items().length > 1 && !this.theme().languageSelectHidden;
  });

  readonly langValue = computed(
    () => this.languageService.currentLang()?.toLowerCase() ?? this.langControl.value.toLowerCase()
  );
  readonly selectedLabel = computed(() => {
    const lang = this.langValue();
    const found = this.items().find((i) => i.lang === lang);
    return found?.label ?? lang?.toUpperCase() ?? '';
  });

  private get storedLanguage() {
    return this.languageService.getStoredLanguageData();
  }

  constructor() {
    effect(() => {
      const lang = this.languageService.currentLang();
      if (!lang) return;
      if (this.langControl.value.toLowerCase() !== lang) {
        this.langControl.setValue(lang, { emitEvent: false });
      }
    });

    this.langControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((lang) => {
      const normalized = lang.toLowerCase();
      if (this.languageService.currentLang() === normalized) return;
      this.languageService.changeLanguage(normalized);
    });

    this.languageService.languageLocalChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newLang) => {
        const stored = this.storedLanguage;
        if (!newLang || !stored?.languagesList?.length) return;

        const normalized = newLang.toLowerCase();
        if (this.langControl.value.toLowerCase() === normalized) return;

        this.applyLanguage(normalized, stored.languagesList);
      });
  }

  ngOnInit(): void {
    this.languageService.context$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadLanguageData();

      const currentLang = this.languageService.currentLang()?.toLowerCase();
      if (currentLang && this.langControl.value !== currentLang) {
        this.langControl.setValue(currentLang, { emitEvent: false });
      }
    });
  }

  private loadLanguageData(): void {
    const stored = this.storedLanguage;
    if (stored?.languagesList?.length) {
      const currentLang = stored.currentLanguage?.toLowerCase() ?? 'pl';
      this.applyLanguage(currentLang, stored.languagesList);
    }
  }

  private applyLanguage(currentLang: string, languages: string[]): void {
    this.languageService
      .filterLanguagesWithAvailableFiles(languages)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowedLanguages) => {
        const normalized = currentLang.toLowerCase();

        if (this.languageService.currentLang() !== normalized) {
          this.languageService
            .setLanguage(normalized)
            .pipe(take(1))
            .subscribe(() => this.loadTranslations(normalized, allowedLanguages));
        } else {
          this.loadTranslations(normalized, allowedLanguages);
        }
      });
  }

  private loadTranslations(currentLang: string, allowedLanguages: string[]): void {
    const normalized = currentLang.toLowerCase();
    if (this.langControl.value.toLowerCase() !== normalized) {
      this.langControl.setValue(normalized, { emitEvent: false });
    }

    this.languageService
      .getTranslatedLanguageItems(allowedLanguages)
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe((raw) => {
        const mapped: LangItem[] = raw.map((x) => {
          const lang = String(x.value).toLowerCase();
          return {
            lang,
            label: lang.toUpperCase(),
          };
        });
        this.items.set(mapped);
        this._langValue.set(this.langControl.value);
      });
  }
}
